/**
 * AimTutorialOverlay — first-visit tutorial.
 *
 * Mobile launch test surfaced that new players instinctively drag the ball
 * straight up and hit the wall every time (the wall is centred, the ball is
 * centred, so a straight kick always blocks). They have no way of knowing
 * about the diagonal + curl mechanic without being told.
 *
 * This overlay shows once per browser (localStorage flag), introduces the
 * three input axes, and dismisses on "Got it" or backdrop click. After that
 * it never appears again unless the flag is cleared. The overlay does NOT
 * gate gameplay — pointer events on the canvas underneath still work, so a
 * power user can ignore it and play through. Pointer-events on the
 * backdrop are absorbed (closing the modal) but pointer-events on the
 * `pointer-events-none` panel below the buttons are not.
 */
import { useEffect, useState } from "react";

const LS_KEY = "curvle.aim_tutorial_seen.v1";

function hasSeenTutorial(): boolean {
  try {
    return window.localStorage.getItem(LS_KEY) === "1";
  } catch {
    // Private mode / disabled storage — fall through and show every time;
    // better that than crash.
    return false;
  }
}

function markSeen(): void {
  try {
    window.localStorage.setItem(LS_KEY, "1");
  } catch {
    /* noop */
  }
}

interface AimTutorialOverlayProps {
  /** Gate the tutorial on the puzzle being loaded — no point flashing
   *  it over a "Loading…" screen. */
  ready: boolean;
}

export function AimTutorialOverlay({ ready }: AimTutorialOverlayProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!ready) return;
    if (hasSeenTutorial()) return;
    // Brief delay so the canvas paints first; the tutorial lands as a
    // deliberate overlay rather than blocking the very first paint.
    const id = window.setTimeout(() => setOpen(true), 400);
    return () => window.clearTimeout(id);
  }, [ready]);

  const dismiss = () => {
    markSeen();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="aim-tutorial-title"
      onClick={dismiss}
    >
      <div
        className="w-[min(28rem,92%)] rounded-3xl bg-emerald-950/95 p-6 text-white shadow-2xl ring-1 ring-amber-300/30"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="aim-tutorial-title"
          className="text-center text-2xl font-bold tracking-tight"
        >
          How to take the kick
        </h2>
        <p className="mt-1 text-center text-sm opacity-70">
          One free kick a day. Make it count.
        </p>

        <ul className="mt-5 space-y-3.5">
          <li className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-full bg-amber-300/15 text-lg ring-1 ring-amber-300/30"
            >
              ↑
            </span>
            <div>
              <p className="text-sm font-semibold">Drag UP for power</p>
              <p className="text-xs opacity-70">
                The longer you drag, the harder you kick. A short drag is a
                pass; a long drag is a screamer.
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-full bg-amber-300/15 text-lg ring-1 ring-amber-300/30"
            >
              ↗
            </span>
            <div>
              <p className="text-sm font-semibold">Drag DIAGONALLY to aim</p>
              <p className="text-xs opacity-70">
                A straight-up kick goes through the centre of the wall. Drag
                up-and-left or up-and-right to aim around it.
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-full bg-amber-300/15 text-lg ring-1 ring-amber-300/30"
            >
              ↻
            </span>
            <div>
              <p className="text-sm font-semibold">Swing sideways for curl</p>
              <p className="text-xs opacity-70">
                While dragging, sweep your finger left or right to bend the
                ball around the wall — real Magnus-effect physics.
              </p>
            </div>
          </li>
        </ul>

        <p className="mt-5 rounded-xl bg-white/5 px-3 py-2 text-center text-xs opacity-80">
          Watch the dotted line while you drag — that&rsquo;s your trajectory
          preview.
        </p>

        <button
          type="button"
          className="mt-5 w-full rounded-full bg-amber-300 px-5 py-2.5 font-semibold text-emerald-950 transition hover:bg-amber-200"
          onClick={dismiss}
          autoFocus
        >
          Got it — let me kick
        </button>
      </div>
    </div>
  );
}
